/**
 * Firebase Phone Authentication service.
 *
 * Wraps Firebase's signInWithPhoneNumber flow so the rest of the app can
 * call sendPhoneOtp / verifyPhoneOtp without knowing Firebase internals.
 *
 * Provider gate:
 *   REACT_APP_SMS_PROVIDER=firebase (default) → this module is active
 *   REACT_APP_SMS_PROVIDER=custom             → isFirebaseSms() returns false
 *
 * Usage pattern:
 *   1. Render a <div id="<containerId>" /> in your component JSX.
 *   2. Call initRecaptcha(containerId) before the first sendPhoneOtp call.
 *      Re-call it if the container is re-mounted (e.g. dialog open/close).
 *   3. Call sendPhoneOtp(e164Phone) to trigger Firebase SMS delivery.
 *   4. User enters the 6-digit code.
 *   5. Call verifyPhoneOtp(otp) → returns a Firebase ID token string.
 *   6. Send that token to the backend /login_firebase_phone (auth) or
 *      /profile/phone (PUT) or /profile/change_password (POST).
 *
 * Free tier limits (Firebase Spark plan):
 *   India (+91): limited daily SMS verifications
 *   UK    (+44): limited daily SMS verifications
 *   Upgrade to Blaze (pay-as-you-go) for higher volume.
 */

import { initializeApp, getApps, getApp } from 'firebase/app';
import { getAuth, signInWithPhoneNumber, RecaptchaVerifier } from 'firebase/auth';
import { initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check';

const SMS_PROVIDER = process.env.REACT_APP_SMS_PROVIDER || 'firebase';

/**
 * Returns true when Firebase Phone Auth is the active SMS provider and the
 * Firebase configuration is present in env vars.
 */
export function isFirebaseSms() {
  return (
    SMS_PROVIDER === 'firebase' &&
    Boolean(process.env.REACT_APP_FIREBASE_API_KEY)
  );
}

// Lazily initialise the Firebase app once.
function _getAuth() {
  if (!isFirebaseSms()) return null;
  const config = {
    apiKey:     process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId:  process.env.REACT_APP_FIREBASE_PROJECT_ID,
    appId:      process.env.REACT_APP_FIREBASE_APP_ID,
  };
  const isNew = !getApps().length;
  const app = isNew ? initializeApp(config) : getApp();

  // App Check with reCAPTCHA v3 — required for Phone Auth token validation.
  if (isNew && process.env.REACT_APP_RECAPTCHA_V3_SITE_KEY) {
    initializeAppCheck(app, {
      provider: new ReCaptchaV3Provider(process.env.REACT_APP_RECAPTCHA_V3_SITE_KEY),
      isTokenAutoRefreshEnabled: true,
    });
  }

  return getAuth(app);
}

// Module-level singletons — safe because Auth and Profile pages are never
// mounted simultaneously, and within Profile only one phone flow runs at a time.
let _verifier = null;
let _confirmation = null;

/**
 * Create (or recreate) an invisible reCAPTCHA verifier.
 *
 * The container is appended directly to document.body so it lives outside
 * React's render tree — React re-renders won't destroy it mid-challenge.
 * The containerId parameter is kept for API compatibility but is ignored.
 */
export function initRecaptcha(_containerId) {
  const auth = _getAuth();
  if (!auth) return;
  if (_verifier) {
    try { _verifier.clear(); } catch {}
    _verifier = null;
  }
  // Remove any leftover container from a previous call.
  const old = document.getElementById('_fb_recaptcha_root');
  if (old) old.remove();

  const container = document.createElement('div');
  container.id = '_fb_recaptcha_root';
  container.style.cssText = '';
  document.body.appendChild(container);

  _verifier = new RecaptchaVerifier(auth, container, { size: 'invisible' });
}

/**
 * Send a phone OTP via Firebase.  The invisible reCAPTCHA resolves automatically.
 *
 * @param {string} e164Phone - Phone in E.164 format, e.g. '+447911123456'
 * @returns {Promise<void>}
 * @throws if Firebase is not configured or reCAPTCHA is not initialised
 */
export async function sendPhoneOtp(e164Phone) {
  const auth = _getAuth();
  if (!auth) throw new Error('Firebase is not configured');
  if (!_verifier) throw new Error('Call initRecaptcha() before sendPhoneOtp()');
  _confirmation = await signInWithPhoneNumber(auth, e164Phone, _verifier);
}

/**
 * Verify the OTP entered by the user and return a Firebase ID token.
 * The token is sent to the backend for server-side verification.
 *
 * @param {string} otp - 6-digit code from the SMS
 * @returns {Promise<string>} Firebase ID token
 * @throws if OTP is wrong or no pending confirmation exists
 */
export async function verifyPhoneOtp(otp) {
  if (!_confirmation) throw new Error('No pending OTP — call sendPhoneOtp() first');
  const credential = await _confirmation.confirm(otp);
  return credential.user.getIdToken();
}

/**
 * Clear the reCAPTCHA verifier and pending confirmation result.
 * Call this when a phone auth dialog/panel is closed without completing.
 */
export function clearFirebasePhone() {
  if (_verifier) {
    try { _verifier.clear(); } catch {}
    _verifier = null;
  }
  _confirmation = null;
  const container = document.getElementById('_fb_recaptcha_root');
  if (container) container.remove();
}
